<script>
import { mapActions, mapState } from 'vuex';
import { unref } from 'vue';
import supplierCommitmentTypes from '@enums/supplier-commitment-types';
import filters from '@enums/filters';
import { findIndex, uniqueId } from 'lodash';
import routeNames from '@enums/route-names';
import filterBar from '@/js/components/filter-bar.vue';
import filterBarMixin from '../../mixins/suppliers-filter-bar-mixin.vue';
import navigation from '@/js/navigation';

const { categories, year, storeGroups } = unref(filters);
const { supplierCommitmentsView } = unref(routeNames);
const { totalSpend } = unref(supplierCommitmentTypes);

export default {
    extends: filterBar,
    mixins: [filterBarMixin],
    emits: ['change-categories-filter'],
    events: {
        onSupplierCommitmentTabChanged() {
            this.updateFilters({ isSupplierCommitmentPage: true });
        },
    },

    computed: {
        ...mapState([
            'totalSpendSupplierCommitmentsFilterDefinitions',
            'defaultSuppliersFilterDefinitions',
        ]),
        ...mapState('supplierCommitments', ['activeSupplierCommitmentsSection']),

        isTotalSpendTab() {
            return this.activeSupplierCommitmentsSection === totalSpend;
        },
    },

    watch: {
        $route(to) {
            const isSupplierCommitmentPage = to.name === supplierCommitmentsView;
            this.updateFilters({ isSupplierCommitmentPage });
        },
    },

    created() {
        this.updateFilters({ isSupplierCommitmentPage: true });
    },

    methods: {
        ...mapActions([
            'resetSuppliersFilters',
            'suppliersFilterDefinitionsSetter',
            'suppliersFiltersSetter',
        ]),
        ...mapActions('supplierCommitments', [
            'fetchSupplierCommitmentsAndSuppliersByCategory',
            'clearSelectedSupplierCommitment',
            'resetSupplierCommitmentsState',
            'fetchTargetsData',
        ]),
        ...mapActions('variableFundingAgreements', ['resetVariableFundingAgreementsState']),

        onFilterValueChange({ filterKey }) {
            if (filterKey === categories || filterKey === year) {
                // remove old supplier filter for updated category
                this.removeSupplierFilter();
                // set selected supplier-commitment to null
                this.clearSelectedSupplierCommitment();

                this.$emit('change-categories-filter');

                // fetch supplierCommitments and suppliers by category
                this.fetchSupplierCommitmentsAndSuppliersByCategory(this.filters);
            }

            // fetch targetsData for totalSpend tab only
            if (
                this.isTotalSpendTab &&
                (filterKey === categories || filterKey === year || filterKey === storeGroups)
            ) {
                this.fetchTargetsData(this.filters);
            }
        },

        resetFilters({ filterId }) {
            // if category filter is not selected, we show empty list of
            // variable-funding-agreements and supplier-commitments
            this.resetSupplierCommitmentsState();
            this.resetVariableFundingAgreementsState();
            this.clearSelectedSupplierCommitment();
            navigation.toSupplierCommitmentsView({ router: this.$router });
            return this.resetSuppliersFilters({ filterId });
        },

        updateFilters({ isSupplierCommitmentPage }) {
            const storeGroupFilterIndex = findIndex(this.filters, { filterKey: storeGroups });
            const yearFilterIndex = findIndex(this.filters, { filterKey: year });

            if (storeGroupFilterIndex !== -1) {
                this.removeFilter(storeGroupFilterIndex);
            }

            if (yearFilterIndex !== -1) {
                this.removeFilter(yearFilterIndex);
            }

            if (isSupplierCommitmentPage && this.activeSupplierCommitmentsSection === totalSpend) {
                this.suppliersFilterDefinitionsSetter({
                    filterDefinitions: this.totalSpendSupplierCommitmentsFilterDefinitions,
                });
                this.suppliersFiltersSetter({
                    filters: [
                        {
                            filterKey: year,
                            filterValue: new Date().getFullYear().toString(),
                            filterId: uniqueId(),
                            filterType: 'select',
                        },
                        ...this.filters,
                    ],
                });

                this.fetchTargetsData(this.filters);
            } else {
                this.suppliersFilterDefinitionsSetter({
                    filterDefinitions: this.defaultSuppliersFilterDefinitions,
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.filter-bar {
    @include promo-component-border($top: true);

    width: 100%;
    padding: 1rem;
    display: flex;
    background-color: $promo-grey-blue-tint;
    max-height: 5.1rem;

    &__grouping {
        display: flex;
        flex: 1;
        align-items: flex-start;

        & > * {
            margin-right: 1rem;
        }
    }

    :deep(.filter-bar__child-filter, .filter-bar__parent-filter) {
        input {
            min-width: 12rem !important;
        }
    }

    :deep(.filter-bar__parent-filter) {
        margin-right: 1rem;
    }

    &__parent-filter {
        margin-right: 1rem;
    }

    &__label {
        color: $filter-bar-text-colour;
        line-height: 2.8rem;
        font-size: 1.4rem;
        font-weight: 300;
    }

    &__main-select {
        flex: 0 1 20rem;
    }

    &__search-field {
        flex: 0 1 24rem;
        margin-left: auto;
    }

    &__cancel-button {
        margin-top: 0.2rem;
        padding-top: 0;
        margin-left: 1rem;
        background: none;
        box-shadow: none;

        :deep(.v-btn__overlay) {
            display: none !important;
        }
    }

    &__add-button {
        line-height: 2.8rem;
    }
}
</style>
